import React, { useEffect } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { IntakeForm } from "@/components/public/IntakeForm";
import { Phone, Check, CalendarHeart } from "lucide-react";

const CONTAINER = "max-w-6xl mx-auto px-4 sm:px-6 lg:px-8";
const telHref = (phone) => `tel:${(phone || "").replace(/[^0-9+]/g, "")}`;

const SERVICE_MAP = {
  sell_home: "sell_home",
  home_safety: "home_safety",
  move_into_my_home: "move_into_my_home",
  senior_placement: "senior_placement",
  downsizing: "downsizing",
  home_repairs: "home_repairs",
};

const POINTS = [
  "A full hour with a caring transition guide",
  "Completely free — no cost, no obligation",
  "One person who stays with you start to finish",
  "A clear, personalized plan for your family",
];

export default function Book() {
  const { content } = useOutletContext();
  const [params] = useSearchParams();
  const service = SERVICE_MAP[params.get("service")] || "";
  const from = params.get("from");
  const campaign = from === "final_cta" ? "Final CTA" : params.get("service") ? "Service Card" : "Homepage Hero";
  const phone = content?.phone;

  useEffect(() => {
    document.title = "Book a Free Consultation · MovingMyMama";
  }, []);

  return (
    <div data-testid="book-page" className="py-14 sm:py-20">
      <div className={CONTAINER}>
        <div className="grid gap-12 lg:grid-cols-12 lg:items-start">
          <div className="lg:col-span-5">
            <p className="text-[14px] font-semibold uppercase tracking-[0.14em] text-[#9C1E22]">Free 1-hour consultation</p>
            <h1 className="mt-3 text-4xl sm:text-5xl font-display font-bold tracking-tight text-[#1C4358] leading-tight">
              Let's make the next right move for your loved one — together.
            </h1>
            <p className="mt-5 text-lg text-[#4B5563] leading-relaxed">
              Tell us a little about what's happening. A caring guide will reach out to listen, answer your
              questions, and help you find a calm, clear path forward.
            </p>
            <ul className="mt-8 space-y-3.5">
              {POINTS.map((p, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#2F7D5A]/12 text-[#2F7D5A]">
                    <Check size={16} />
                  </span>
                  <span className="text-[17px] text-[#252221]">{p}</span>
                </li>
              ))}
            </ul>
            {phone && (
              <a
                href={telHref(phone)}
                data-testid="book-call-btn"
                className="mmm-tap mt-8 inline-flex items-center gap-2 px-6 h-14 rounded-full text-[16px] font-semibold text-[#1C4358] border border-[#1C4358]/25 hover:bg-[#1C4358]/6 transition-colors"
              >
                <Phone size={18} /> Prefer to talk now? Call {phone}
              </a>
            )}
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-[1.2rem] bg-white border border-[#1C4358]/10 shadow-md shadow-[#1C4358]/10 p-6 sm:p-9">
              <div className="flex items-center gap-3 mb-6">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#9C1E22]/10 text-[#9C1E22]">
                  <CalendarHeart size={24} />
                </span>
                <div>
                  <h2 className="text-xl font-display font-semibold text-[#1C4358]">Request your free consultation</h2>
                  <p className="text-[14px] text-[#4B5563]">We usually respond the same day.</p>
                </div>
              </div>
              <IntakeForm
                cta="Book a Free 1-Hour Consultation"
                source="Free Consultation Form"
                campaign={campaign}
                defaultService={service}
                submitLabel="Request my free consultation"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
